import { Tile } from "@assetwatch/ui";
import type { ReactNode } from "react";

export type WorkPageProps = {
  /** Title row: page heading and primary actions. */
  header: ReactNode;
  /** Inline alerts shown between the header and the tabs. */
  alerts?: ReactNode;
  /** Page tabs, when the page has views. */
  tabs?: ReactNode;
  /** Search and filter bar, usually `SearchFilterToolbar`. */
  toolbar?: ReactNode;
  /** Main content. It fills the remaining page height. */
  children: ReactNode;
};

/**
 * Full-height page frame for Work screens: header, alerts, tabs, toolbar, then
 * a content region that fills the space left below them. Spacing follows the
 * design reference (header to tabs 8, tabs to toolbar 8, toolbar to content
 * 12). The height comes from the Work-scoped fill-height rule in
 * `styles.css`, which targets `data-fill-page`; do not add viewport
 * calculations to pages.
 *
 * @ds-deviation: the design system has no page-fill layout or split-view
 * primitive; `AppShellContent` hands pages an unsized block and product work
 * must not edit `packages/ui`. Proposed resolution: promote a page-fill frame
 * and a split view into `@assetwatch/ui` (alongside a fill-height
 * `AppShellContent`), then replace this file.
 */
function WorkPage({ header, alerts, tabs, toolbar, children }: WorkPageProps) {
  return (
    <div
      className="flex h-full min-h-0 min-w-0 flex-1 flex-col"
      data-fill-page=""
      data-slot="work-page"
    >
      <div className="shrink-0" data-slot="work-page-header">
        {header}
      </div>
      {alerts ? (
        <div className="mt-(--spacing-8) shrink-0" data-slot="work-page-alerts">
          {alerts}
        </div>
      ) : null}
      {tabs ? (
        <div className="mt-(--spacing-8) shrink-0" data-slot="work-page-tabs">
          {tabs}
        </div>
      ) : null}
      {toolbar ? (
        <div
          className="mt-(--spacing-8) shrink-0"
          data-slot="work-page-toolbar"
        >
          {toolbar}
        </div>
      ) : null}
      <div
        className="mt-(--spacing-12) flex min-h-0 flex-1 flex-col"
        data-slot="work-page-content"
      >
        {children}
      </div>
    </div>
  );
}

const splitColumns = {
  workOrders: "lg:grid-cols-[minmax(340px,0.8fr)_minmax(0,1.6fr)]",
  requests: "lg:grid-cols-[minmax(340px,38%)_minmax(0,1fr)]",
  procedures: "lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]",
} as const;

export type WorkSplitLayout = keyof typeof splitColumns;

export type WorkSplitViewProps = {
  /** Column template for the list and detail panels at `lg` and wider. */
  layout: WorkSplitLayout;
  list: ReactNode;
  detail: ReactNode;
};

/**
 * Master/detail split. Both panels stay the full height of the content region
 * even when their content is short; each panel's inner region owns its own
 * scrolling. Below `lg` the panels stack and the page scrolls naturally.
 *
 * Panels are the DS `Tile` or a bordered section; a `Tile` child is stretched
 * to the panel because `Tile` accepts no `className`.
 */
function WorkSplitView({ layout, list, detail }: WorkSplitViewProps) {
  const panelClassName =
    "flex min-h-[24rem] min-w-0 flex-col overflow-hidden lg:min-h-0 [&>*]:min-h-0 [&>*]:flex-1 [&>[data-slot=tile]]:flex [&>[data-slot=tile]]:flex-col [&>[data-slot=tile]]:overflow-hidden";

  return (
    <div
      className={`grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-(--spacing-16) lg:grid-rows-[minmax(0,1fr)] ${splitColumns[layout]}`}
      data-slot="work-split-view"
    >
      <div className={panelClassName} data-slot="work-split-list">
        {list}
      </div>
      <div className={panelClassName} data-slot="work-split-detail">
        {detail}
      </div>
    </div>
  );
}

/**
 * Full-height bordered card for a single content region (for example a data
 * table). The caller owns the scrolling inside it. A DS `Tile` is stretched to
 * the region because `Tile` accepts no `className`.
 */
function WorkPanel({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-[24rem] min-w-0 flex-1 flex-col lg:min-h-0 [&>[data-slot=tile]]:flex [&>[data-slot=tile]]:min-h-0 [&>[data-slot=tile]]:flex-1 [&>[data-slot=tile]]:flex-col [&>[data-slot=tile]]:overflow-hidden"
      data-slot="work-panel"
    >
      <Tile hasBorder padding="none" radius="md" width="full">
        {children}
      </Tile>
    </div>
  );
}

/**
 * Loading, error and empty states for a page's content region. It fills the
 * same space as the loaded content so the layout does not jump when data
 * arrives.
 */
function WorkPageState({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  /** Use `negative` for error states; loading and empty states stay neutral. */
  tone?: "neutral" | "negative";
}) {
  return (
    <div
      className={`flex min-h-[24rem] min-w-0 flex-1 flex-col items-center justify-center gap-(--spacing-8) rounded-(--radius-md) border border-outline-subtle p-(--spacing-24) text-center lg:min-h-0 ${
        tone === "negative"
          ? "bg-negative-container text-negative-on-container"
          : "bg-surface-container-lowest"
      }`}
      data-slot="work-page-state"
      data-tone={tone}
    >
      {children}
    </div>
  );
}

export { WorkPage, WorkPageState, WorkPanel, WorkSplitView };
